import { createFeatureSelector, createSelector } from '@ngrx/store';
import { RESET_PASSWORD_FEATURE_KEY } from './reset-password.constants';
import { ResetPasswordState, ResetPasswordView } from './reset-password.models';
import { RESET_PASSWORD_CONFIG } from '../reset-password.config';

export const selectResetPasswordState = createFeatureSelector<ResetPasswordState>(
  RESET_PASSWORD_FEATURE_KEY,
);

export const selectStep = createSelector(selectResetPasswordState, (state) => state.step);

export const selectLoading = createSelector(selectResetPasswordState, (state) => state.loading);

export const selectTokenStatus = createSelector(
  selectResetPasswordState,
  (state) => state.tokenStatus,
);

export const selectResetToken = createSelector(
  selectResetPasswordState,
  (state) => state.resetToken,
);

export const selectSubmittedEmail = createSelector(
  selectResetPasswordState,
  (state) => state.submittedEmail,
);

export const selectResendRemaining = createSelector(
  selectResetPasswordState,
  (state) => state.resendRemainingSeconds,
);

export const selectResendAttempts = createSelector(
  selectResetPasswordState,
  (state) => state.resendAttempts,
);

export const selectResetError = createSelector(selectResetPasswordState, (state) => state.error);

export const selectRetry = createSelector(selectResetPasswordState, (state) => state.retry);

export const selectView = createSelector(selectResetPasswordState, (state): ResetPasswordView => {
  if (state.error === 'NETWORK') {
    return 'network-error';
  }

  if (state.tokenStatus === 'verifying') {
    return 'loading';
  }

  if (state.tokenStatus === 'invalid' || state.error === 'EXPIRED_TOKEN') {
    return 'expired';
  }

  return state.step;
});

export const selectResendDisabled = createSelector(
  selectLoading,
  selectResendRemaining,
  (loading, remaining) => loading || remaining > 0,
);

export const selectResendWarningVisible = createSelector(
  selectResendAttempts,
  (attempts) => attempts >= RESET_PASSWORD_CONFIG.RESEND_LONG_COOLDOWN_ATTEMPT,
);

export const selectResendCountdownText = createSelector(
  selectResendRemaining,
  selectResendAttempts,
  (remainingSeconds, attempts) => {
    if (attempts >= RESET_PASSWORD_CONFIG.RESEND_LONG_COOLDOWN_ATTEMPT) {
      const displaySeconds = Math.max(0, remainingSeconds - 1);
      const minutes = Math.floor(displaySeconds / 60);
      const seconds = displaySeconds % 60;

      return `${minutes}:${seconds.toString().padStart(2, '0')} min`;
    }

    return `${remainingSeconds} sec`;
  },
);

export const selectNewPasswordCheck = createSelector(
  selectResetPasswordState,
  (state) => state.newPasswordCheck,
);

export const selectNewPasswordConfirmedDifferent = createSelector(
  selectNewPasswordCheck,
  (check) => check === 'different',
);

export const selectShowSamePasswordMessage = createSelector(
  selectResetError,
  selectNewPasswordCheck,
  (error, check) => check === 'same' || error === 'SAME_PASSWORD',
);
